import React, { useCallback, useEffect, useRef, useState } from "react";

import styled from "styled-components";

import SpacePaymentNewModal from "components/page/modal/SpacePaymentNewModal";
import SpaceBar from "components/templates/SpaceBar";
import SpaceMenuBar, {
    SIDE_WIDTH_AUTOFOLD,
    SIDE_WIDTH_BASE,
    SIDE_WIDTH_MAX,
    SIDE_WIDTH_MIN,
} from "components/templates/SpaceMenuBar";

import Constants from "utils/constants";
import { useDevice } from "utils/device";

const SIDEBAR_DATA_KEY = "sidebarData";
const DEFAULT_HEADER_HEIGHT = 56;
const DEFAULT_MAIN_MIN_WIDTH = 760;

const Body = styled.div`
    position: relative;
    display: flex;
    width: 100%;
    height: ${({ $headerHeight }) => `calc(100vh - ${$headerHeight}px)`};
    overflow: hidden;
    background-color: var(--color-White);
`;

const MainContainer = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    min-width: ${({ $minWidth }) => `${$minWidth}px`};
    height: 100%;
    overflow: hidden;
    background-color: ${({ $backgroundColor }) => $backgroundColor};
`;

function getInitialSidebarData() {
    if (typeof window === "undefined") {
        return { isFold: false, width: SIDE_WIDTH_BASE };
    }
    const sidebarData = JSON.parse(localStorage.getItem(SIDEBAR_DATA_KEY) || "null");
    const isFold = sidebarData?.f || false;
    return {
        isFold,
        width: isFold ? SIDE_WIDTH_MIN : Math.max(SIDE_WIDTH_BASE, sidebarData?.w || SIDE_WIDTH_BASE),
    };
}

function formatBytes(bytes, decimals = 2) {
    if (!bytes) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

function SpaceDetailPageLayout({
    children,
    headerHeight = DEFAULT_HEADER_HEIGHT,
    mainBackgroundColor = "var(--color-Base1)",
    mainMinWidth = DEFAULT_MAIN_MIN_WIDTH,
    navigate,
    onBack,
    sidebarContent,
    sidebarTitle,
    sidebarTitleAction = null,
    sidebarTitleLabel,
    spaceBarMode = "reference-detail",
    spaceBarTopContent = null,
    spaceBarBottomContent = null,
    spaceMenuFoldRef = null,
    spaceUuid,
    user,
}) {
    const device = useDevice();
    const wBarRef = useRef(null);
    const [isSpaceMenuFold, setIsSpaceMenuFold] = useState(() => getInitialSidebarData().isFold);
    const [spaceMenuWidth, setSpaceMenuWidth] = useState(() => getInitialSidebarData().width);
    const [spaceMenuResize, setSpaceMenuResize] = useState(false);
    const [spaceMenuHover, setSpaceMenuHover] = useState(false);
    const [showPaidPlanSubscribeModal, setShowPaidPlanSubscribeModal] = useState(false);

    const volume = user?.current_space?.volume_info;
    const isVolumeWarn = volume?.max ? ((volume.used / volume.max) * 100).toFixed(0) >= 90 : false;

    const saveSidebarSize = useCallback(() => {
        const widthValue = wBarRef.current?.style?.width;
        const width = typeof widthValue === "string" ? parseInt(widthValue.replace("px", ""), 10) : spaceMenuWidth;
        localStorage.setItem(
            SIDEBAR_DATA_KEY,
            JSON.stringify({
                w: width || SIDE_WIDTH_BASE,
                f: isSpaceMenuFold,
            }),
        );
    }, [isSpaceMenuFold, spaceMenuWidth]);

    const saveSidebarFold = useCallback(
        (fold, width = spaceMenuWidth) => {
            localStorage.setItem(
                SIDEBAR_DATA_KEY,
                JSON.stringify({
                    w: width || SIDE_WIDTH_BASE,
                    f: fold,
                }),
            );
        },
        [spaceMenuWidth],
    );

    const startSpaceMenuResizing = () => {
        if (!isSpaceMenuFold) {
            setSpaceMenuResize(true);
        }
    };

    useEffect(() => {
        if (!spaceMenuResize) return undefined;

        const applySpaceMenuResizing = (event) => {
            if (!wBarRef.current) return;
            const nextWidth = event.clientX - wBarRef.current.getBoundingClientRect().left;
            setSpaceMenuWidth(Math.min(Math.max(nextWidth, SIDE_WIDTH_MIN), SIDE_WIDTH_MAX));
        };
        const stopSpaceMenuResizing = () => {
            setSpaceMenuResize(false);
            saveSidebarSize();
        };

        window.addEventListener("mousemove", applySpaceMenuResizing);
        window.addEventListener("mouseup", stopSpaceMenuResizing);
        return () => {
            window.removeEventListener("mousemove", applySpaceMenuResizing);
            window.removeEventListener("mouseup", stopSpaceMenuResizing);
        };
    }, [saveSidebarSize, spaceMenuResize]);

    useEffect(() => {
        if (spaceMenuResize === false && !isSpaceMenuFold && spaceMenuWidth < SIDE_WIDTH_AUTOFOLD) {
            setIsSpaceMenuFold(true);
            setSpaceMenuWidth(SIDE_WIDTH_MIN);
            saveSidebarFold(true);
        }
    }, [isSpaceMenuFold, saveSidebarFold, spaceMenuResize, spaceMenuWidth]);

    const toggleSpaceMenu = (event) => {
        event?.stopPropagation?.();
        if (isSpaceMenuFold) {
            const savedSidebarData = JSON.parse(localStorage.getItem(SIDEBAR_DATA_KEY) || "null");
            const nextWidth = Math.max(SIDE_WIDTH_BASE, savedSidebarData?.w || SIDE_WIDTH_BASE);
            setIsSpaceMenuFold(false);
            setSpaceMenuWidth(nextWidth);
            saveSidebarFold(false, nextWidth);
            return;
        }
        setIsSpaceMenuFold(true);
        setSpaceMenuWidth(SIDE_WIDTH_MIN);
        saveSidebarFold(true);
    };

    useEffect(() => {
        if (!spaceMenuFoldRef) return;
        spaceMenuFoldRef.current = {
            isFold: isSpaceMenuFold,
            setFold: (nextFold) => {
                if (nextFold) {
                    setIsSpaceMenuFold(true);
                    setSpaceMenuWidth(SIDE_WIDTH_MIN);
                    saveSidebarFold(true);
                } else {
                    const savedSidebarData = JSON.parse(localStorage.getItem(SIDEBAR_DATA_KEY) || "null");
                    const nextWidth = Math.max(SIDE_WIDTH_BASE, savedSidebarData?.w || SIDE_WIDTH_BASE);
                    setIsSpaceMenuFold(false);
                    setSpaceMenuWidth(nextWidth);
                    saveSidebarFold(false, nextWidth);
                }
            },
            toggle: () => {
                if (isSpaceMenuFold) {
                    const savedSidebarData = JSON.parse(localStorage.getItem(SIDEBAR_DATA_KEY) || "null");
                    const nextWidth = Math.max(SIDE_WIDTH_BASE, savedSidebarData?.w || SIDE_WIDTH_BASE);
                    setIsSpaceMenuFold(false);
                    setSpaceMenuWidth(nextWidth);
                    saveSidebarFold(false, nextWidth);
                } else {
                    setIsSpaceMenuFold(true);
                    setSpaceMenuWidth(SIDE_WIDTH_MIN);
                    saveSidebarFold(true);
                }
            },
        };
    }, [isSpaceMenuFold, saveSidebarFold, spaceMenuFoldRef]);

    return (
        <>
            <Body $headerHeight={headerHeight}>
                <SpaceBar
                    mode={spaceBarMode}
                    onBack={onBack}
                    topContent={spaceBarTopContent}
                    bottomContent={spaceBarBottomContent}
                />
                <SpaceMenuBar
                    customMode
                    customTitleLabel={sidebarTitleLabel}
                    customTitle={sidebarTitle}
                    customTitleAction={sidebarTitleAction}
                    customContent={sidebarContent}
                    showCustomVolumeSection
                    device={device}
                    wBarRef={wBarRef}
                    isFold={isSpaceMenuFold}
                    wBarSize={spaceMenuWidth}
                    wBarResize={spaceMenuResize}
                    wBarHover={spaceMenuHover}
                    startWBarResizing={startSpaceMenuResizing}
                    setWBarHover={setSpaceMenuHover}
                    toggleSidebar={toggleSpaceMenu}
                    space_uuid={spaceUuid}
                    navigate={navigate}
                    user={user}
                    setShowPaidPlanSubscribeModal={setShowPaidPlanSubscribeModal}
                    volume={volume}
                    isVolumeWarn={isVolumeWarn}
                    formatBytes={formatBytes}
                />
                <MainContainer
                    $backgroundColor={mainBackgroundColor}
                    $minWidth={mainMinWidth}
                    style={{
                        left: `${Constants.SPACE_BAR_WIDTH + spaceMenuWidth}px`,
                        width: `calc(100% - ${Constants.SPACE_BAR_WIDTH + spaceMenuWidth}px)`,
                    }}
                >
                    {children}
                </MainContainer>
            </Body>
            {showPaidPlanSubscribeModal && (
                <SpacePaymentNewModal user={user} exitModal={() => setShowPaidPlanSubscribeModal(false)} />
            )}
        </>
    );
}

export default SpaceDetailPageLayout;
